import { Injectable, NgZone } from "@angular/core";
import { App, RestoredListenerEvent } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Router } from "@angular/router";
import { Document } from "../../models/document";
import { User } from "../../models/user";
import { RegisterService } from "../../services/register.service";
import { StorageService } from "../../services/storage.service";
import { SystemService } from "../../services/system.service";

const KEY = "pendingDocumentCamera";
const TTL = 30 * 60 * 1000;
export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;

export function cameraDraft(client: User): User {
  const draft: User = JSON.parse(JSON.stringify(client));
  if (draft.responsible) delete draft.responsible.password;
  return draft;
}

@Injectable({ providedIn: "root" })
export class CameraRecoveryService {
  private initialization?: Promise<void>;

  constructor(private storage: StorageService, private register: RegisterService,
    private zone: NgZone, private router: Router, private system: SystemService) {}

  initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return Promise.resolve();
    if (!this.initialization) {
      this.initialization = App.addListener("appRestoredResult", event => {
        void this.restore(event).catch(error => console.warn("[Camera] Falha ao recuperar documento", error));
      }).then(async () => {
        const pending = await this.storage.get(KEY);
        if (pending && Date.now() - pending.createdAt > TTL) await this.clear();
      }).catch(error => { this.initialization = undefined; throw error; });
    }
    return this.initialization;
  }

  async prepare(type: Document["type"]): Promise<void> {
    await this.initialize();
    await this.storage.set(KEY, { type, createdAt: Date.now(), client: cameraDraft(this.register.client) });
  }

  clear(): Promise<void> { return this.storage.set(KEY, null); }

  private async restore(event: RestoredListenerEvent): Promise<void> {
    if (event.pluginId !== "Camera" || event.methodName !== "getPhoto") return;
    const pending = await this.storage.get(KEY);
    await this.clear();
    if (!pending || Date.now() - pending.createdAt > TTL || !pending.client?.cpfCnpj) return;
    if (!["identification", "addressProof", "storeFront", "storeInterior"].includes(pending.type)) return;
    const dataUrl = event.success && event.data?.dataUrl;
    const photoRecovered = typeof dataUrl === "string" && dataUrl.startsWith("data:image/") && dataUrl.length <= MAX_DOCUMENT_BYTES * 4 / 3 + 100;
    if (photoRecovered) {
      pending.client.proof = { ...pending.client.proof, [pending.type]: dataUrl };
    }
    await this.zone.run(() => {
      this.register.clearClient(true);
      this.register.client = pending.client;
      return this.system.showAlert("none", "Retomar cadastro",
        photoRecovered ? "O aparelho reiniciou o aplicativo durante a captura. Seu cadastro foi recuperado; confira a foto antes de enviar." :
          "O aparelho reiniciou o aplicativo durante a captura. Seu cadastro foi recuperado; selecione a foto novamente.",
        "Continuar", () => { void this.router.navigateByUrl("/register"); });
    });
  }
}
